import { DateTime } from 'luxon'
import { EmployeeSkill, EmployeeSkillDto, employeeSkillFromDto } from '../../employee_skills/models/employeeSkill'
import { User, UserJson } from '../../users/models/user'
import { Role, RoleJson } from '../../permissions/models/role'

declare const EmployeeIDSymbol: unique symbol
export type EmployeeID = string & { [EmployeeIDSymbol]: never }

type EmployeeSkillsRelation = {
    data: EmployeeSkillDto[]
} | null

type EmployeeUserRelation = {
    data: UserJson | null
} | null

type EmployeeRolesRelation = {
    data: RoleJson[]
} | null

export interface EmployeeJson {
    id: string
    display_name: string
    first_name: string
    last_name: string
    email: string
    image: string

    avatar_url?: string

    email_verified_at?: string
    contact_number_verified_at?: string

    sms_opted_in_at?: string

    roles?: EmployeeRolesRelation

    has_account?: boolean

    employee_skills?: EmployeeSkillsRelation

    archived_at: string | null

    custom_fields?: Record<string, string>

    user?: EmployeeUserRelation

    hire_date?: string | null

    last_active_at?: string | null

    new_app_login_at?: string | null

    has_token?: boolean
}

export class Employee {
    id: EmployeeID
    displayName: string
    firstName: string
    lastName: string
    email: string
    image: string

    avatarUrl?: string

    emailVerifiedAt: DateTime | null
    contactNumberVerifiedAt: DateTime | null

    smsOptedInAt: DateTime | null

    roles: Role[] = []

    hasAccount: boolean = false

    employeeSkills?: EmployeeSkill[]

    archivedAt: DateTime | null

    customFields: Record<string, string> = {}

    user?: User | null

    hireDate: DateTime | null

    lastActiveAt: DateTime | null

    newAppLoginAt?: DateTime | null

    hasToken: boolean = false

    constructor(
        id: string,
        displayName: string,
        firstName: string,
        lastName: string,
        email: string,
        image: string,
        avatarUrl?: string,
        emailVerifiedAt: DateTime | null = null,
        contactNumberVerifiedAt: DateTime | null = null,
        smsOptedInAt: DateTime | null = null,
        roles: Role[] = [],
        hasAccount: boolean = false,
        employeeSkills: EmployeeSkill[] = [],
        archivedAt: DateTime | null = null,
        customFields: Record<string, string> = {},
        user: User | null = null,
        hireDate: DateTime | null = null,
        lastActiveAt: DateTime | null = null,
        newAppLoginAt: DateTime | null = null,
        hasToken: boolean = false
    ) {
        this.id = id as EmployeeID
        this.displayName = displayName
        this.firstName = firstName
        this.lastName = lastName
        this.email = email
        this.image = image
        this.avatarUrl = avatarUrl
        this.emailVerifiedAt = emailVerifiedAt
        this.contactNumberVerifiedAt = contactNumberVerifiedAt
        this.smsOptedInAt = smsOptedInAt
        this.roles = roles
        this.hasAccount = hasAccount
        this.employeeSkills = employeeSkills
        this.archivedAt = archivedAt
        this.customFields = customFields
        this.user = user
        this.hireDate = hireDate
        this.lastActiveAt = lastActiveAt
        this.newAppLoginAt = newAppLoginAt
        this.hasToken = hasToken
    }

    static fromJson(json: EmployeeJson): Employee {
        return new Employee(
            json.id,
            json.display_name,
            json.first_name,
            json.last_name,
            json.email,
            json.image,
            json.avatar_url,
            json.email_verified_at ? DateTime.fromISO(json.email_verified_at) : null,
            json.contact_number_verified_at ? DateTime.fromISO(json.contact_number_verified_at) : null,
            json.sms_opted_in_at ? DateTime.fromISO(json.sms_opted_in_at) : null,
            json.roles?.data ? json.roles.data.map((role) => Role.fromJson(role)) : [],
            json.has_account ?? false,
            json.employee_skills ? json.employee_skills.data.map((employeeSkill) => employeeSkillFromDto(employeeSkill)) : [],
            json.archived_at ? DateTime.fromISO(json.archived_at) : null,
            json.custom_fields ?? {},
            json.user?.data ? User.fromJson(json.user.data) : null,
            json.hire_date ? DateTime.fromISO(json.hire_date) : null,
            json.last_active_at ? DateTime.fromISO(json.last_active_at) : null,
            json.new_app_login_at ? DateTime.fromISO(json.new_app_login_at) : null,
            json.has_token ?? false
        )
    }
}
